import { UploadFileTypeEnum } from 'api-hooks/upload/upload.model';
import { PlusCircleSVG, TrashSVG } from 'common/assets';
import stylesColors from 'common/styles/styles.colors';
import { themedStyled } from 'common/styles/styletron.theme';
import { Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { GuestFormType } from '../form-type';

export default function ApecCard() {
  const { t } = useTranslation();
  const { control, setValue, clearErrors } = useFormContext<GuestFormType>();
  const value = useWatch({ control, name: 'apecCard' });

  const onAddApec = () => {
    setValue('hasApecCard', true);
    setValue('apecCard', {
      id: undefined,
      type: '',
      number: '',
      name: '',
      applicationNumber: '',
      email: '',
      passportNumber: '',
      pin: '',
      expiredAt: null,
      files: [],
    });
    clearErrors('apecCard');
  };

  const onRemoveApec = () => {
    setValue('hasApecCard', false);
    setValue('apecCard', null);
    clearErrors('apecCard');
  };

  const { editable } = useFormState();

  return (
    <>
      <SeparatorVertical $margin={32}>
        <FullContainer
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Text variant="HeadingXSmall">{t('common:APEC')}</Text>
          <EndContent>
            {!value && editable && (
              <Button
                buttonType="secondary"
                onClick={onAddApec}
                startEnhancer={(color, size) => (
                  <PlusCircleSVG size={size} color={color} />
                )}
              >
                {t('common:add')}
              </Button>
            )}
          </EndContent>
        </FullContainer>
      </SeparatorVertical>
      {value ? (
        <>
          <CardSection style={{ paddingBottom: 0 }}>
            <SectionContainer>
              <HalfContainer>
                <Input
                  type="text"
                  name="apecCard.name"
                  label={t('common:name')}
                  placeholder={t('common:name')}
                />
              </HalfContainer>
              <HalfContainer>
                <Input
                  type="text"
                  name="apecCard.email"
                  label={t('common:email')}
                  placeholder={t('common:email')}
                />
              </HalfContainer>
            </SectionContainer>
            <SectionContainer>
              <HalfContainer>
                <Input
                  type="text"
                  name="apecCard.applicationNumber"
                  label={t('common:application_number')}
                  placeholder={t('common:application_number')}
                />
              </HalfContainer>
              <HalfContainer>
                <Input
                  type="text"
                  name="apecCard.number"
                  label={t('common:document_number')}
                  placeholder={t('common:document_number')}
                />
              </HalfContainer>
            </SectionContainer>
            <SectionContainer>
              <HalfContainer>
                <Input
                  type="text"
                  name="apecCard.passportNumber"
                  label={t('common:passport_number')}
                  placeholder={t('common:passport_number')}
                />
              </HalfContainer>
              <HalfContainer>
                <Input
                  type="text"
                  name="apecCard.pin"
                  label={t('common:pin')}
                  placeholder={t('common:pin')}
                />
              </HalfContainer>
            </SectionContainer>
            <SectionContainer>
              <HalfContainer>
                <Input
                  type="date-time"
                  name="apecCard.expiredAt"
                  label={t('common:date_of_expiry')}
                  placeholder={t('common:date_of_expiry')}
                />
              </HalfContainer>
              <HalfContainer>
                <Input
                  type="files"
                  name="apecCard.files"
                  label={t('common:upload_document')}
                  valueKey="file"
                  previewKey="url"
                  fileType={UploadFileTypeEnum.GuestApecCard}
                  required
                />
              </HalfContainer>
            </SectionContainer>
            {editable && (
              <>
                <Button
                  buttonType="secondary"
                  startEnhancer={(color, size) => (
                    <TrashSVG
                      width={size}
                      height={size}
                      color={stylesColors.redDefault}
                    />
                  )}
                  isError
                  onClick={onRemoveApec}
                >
                  {t('common:remove')}
                </Button>
                <SeparatorVertical $margin={14}>
                  <div style={{ width: 2, height: 2 }} />
                </SeparatorVertical>
              </>
            )}
          </CardSection>
          <SeparatorVertical $margin={14}>
            <div style={{ width: 2, height: 2 }} />
          </SeparatorVertical>
        </>
      ) : (
        <Text variant="ParagraphSmall">
          {t('common:you_have_no_document_at_this_moment', {
            extra: t('common:APEC').toLowerCase(),
          })}
        </Text>
      )}
    </>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
});

const EndContent = themedStyled('div', ({ $theme }) => ({
  display: 'flex',
  width: '100%',
  justifyContent: 'center',
  alignItems: 'end',
  flexDirection: 'column',
  [`${$theme.mediaQuery.sm}`]: {
    flex: 1,
    justifyContent: 'end',
    alignItems: 'center',
    flexDirection: 'row',
  },
}));
